import React, { useState, useEffect, useTransition } from 'react';
import { useMediaQuery } from "react-responsive";
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { FaSearch, FaRegLightbulb, FaExternalLinkAlt } from "react-icons/fa";
import { SuspenseModalComponent } from "../../../utils/functions";
import { componentMap } from '../../../components/GlobalComponents/index';

const CommonlyUsedForms = ({ modules, navigationData }) => {
    const [isPending, startTransition] = useTransition();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedComponent, setSelectedComponent] = useState(null);
    const [modalTitle, setModalTitle] = useState('');
    const [access, setAccess] = useState(null);
    const [shouldRenderComponent, setShouldRenderComponent] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const isMobile = useMediaQuery({ maxWidth: 575.98 });
    const isTablet = useMediaQuery({ minWidth: 576, maxWidth: 991.98 });

    useEffect(() => {
        if (!isPending && selectedComponent && modalOpen) {
            setShouldRenderComponent(true);
        }

        if (!modalOpen) {
            setShouldRenderComponent(false);
        }
    }, [isPending, selectedComponent, modalOpen]);

    const moduleRouteToId = (submenuRoute) => {
        const module = navigationData.find(item => item.module_route === submenuRoute);
        return module ? module.role_json : null;
    };

    const handleModuleSelect = (object) => {
        setModalTitle(object.name);
        setShouldRenderComponent(false);

        const module = navigationData.find(item => item.role_json.moduleId === object.moduleId);
        if (module) {
            const Component = componentMap[module.module_route];
            if (Component) {
                const moduleAccess = moduleRouteToId(module.module_route);

                startTransition(() => {
                    setSelectedComponent(() => Component);
                    setAccess(moduleAccess);
                    setModalOpen(true);
                });
            } else {
                console.error(`No component found for module_route: ${module.module_route}`);
            }
        }
    };

    const toggleModal = () => {
        if (modalOpen) {
            setModalOpen(false);
            setTimeout(() => {
                setSelectedComponent(null);
            }, 300);
        } else {
            startTransition(() => {
                setModalOpen(true);
            });
        }
    };

    const filteredModules = modules?.filter(module =>
        module.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const listMaxHeight = isMobile ? '200px' : isTablet ? '250px' : '300px';

    return (
        <>
            <div className="search-container mb-3">
                <input
                    type="search"
                    className="search-input"
                    placeholder={isMobile ? "Find..." : "Find a form..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search forms"
                />
                <FaSearch className="search-icon" aria-hidden="true" />
            </div>

            {isPending && (
                <div className="d-flex align-items-center justify-content-center py-3" aria-live="polite">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    <span>Loading forms...</span>
                </div>
            )}

            {filteredModules.length > 0 ? (
                <TransitionGroup
                    className="list-group"
                    style={{
                        maxHeight: listMaxHeight,
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}
                    aria-label="Available forms"
                >
                    {filteredModules.map((module, index) => (
                        <CSSTransition
                            key={module.id}
                            timeout={200 + (index * 50)}
                            classNames="item"
                        >
                            <div
                                className="list-group-item"
                                onClick={() => handleModuleSelect(module)}
                                aria-disabled={isPending}
                            >
                                <a href="#" onClick={(e) => e.preventDefault()} className="item-text">
                                    <div className="item-icon">
                                        <FaRegLightbulb />
                                    </div>
                                    {module.name}
                                    <FaExternalLinkAlt className="external-icon" aria-hidden="true" />
                                </a>
                            </div>
                        </CSSTransition>
                    ))}
                </TransitionGroup>
            ) : (
                <div className="text-center py-4 text-muted">
                    {modules?.length > 0 ? (
                        <p>No forms match your search criteria</p>
                    ) : (
                        <>
                            <FaRegLightbulb className="mb-2" size={isMobile ? 20 : 24} />
                            <p>No forms available. Please contact your administrator.</p>
                        </>
                    )}
                </div>
            )}

            <SuspenseModalComponent
                isOpen={modalOpen}
                toggle={toggleModal}
                SelectedComponent={shouldRenderComponent ? selectedComponent : null}
                title={modalTitle}
                access={access}
                fullScreen={isMobile}
            />
        </>
    );
};

export default CommonlyUsedForms; 